import React from 'react'
import { Box, Checkbox, IconButton, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { LinkIcon, PencilIcon, TrashIcon } from 'src/Libs/flow/icons'
import { DeviceStatusIconMobile } from './DeviceStatusIcon'
import { getDeviceDefaultName } from '../../../parsers/device-parsers/get-device-default-name'
import { InfoText } from 'src/Libs/flow/components'
import { Device, Storage } from 'src/Libs/flow/installation/forms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DeviceStatusMap, useInstallationDeviceStatuses, useInstallationForm, useStorageBatteryOverview } from 'src/Libs/flow/installation/hooks'
import { BatteryTrainingMode } from 'src/Libs/system-data-handler'
import { DeviceIcon } from 'src/Libs/flow/icons/DeviceIcon'
import { getDeviceParsedStatus } from 'src/Libs/flow/parsers/device-parsers/get-device-parsed-status'
import { getHasNonEmcDevices } from '../../utils'
import { useDevicePairing } from '../../hooks'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

type DevicesTableProps = {
  devices: Device[]
  selectedDeviceIds: { [deviceId: string]: true }
  toggleDeviceSelection: (id: string) => void
  handleDelete: () => void
}

export const DevicesTableMobile: React.FC<DevicesTableProps> = ({ devices, selectedDeviceIds, toggleDeviceSelection, handleDelete }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const hasNonEmcDevices = getHasNonEmcDevices(devices)

  const { control } = useInstallationForm()
  const installationId = useWatch({ control, name: 'id' })

  const storage = devices.find(device => device.deviceCategory === 'Storage' && device.isPaired) as Storage
  const isActiveStorage = !!storage?.deviceId && !!storage?.pairingId
  const { data } = useStorageBatteryOverview(installationId ?? undefined, storage?.storageHardwareDeviceId ?? null, isActiveStorage)
  const trainingMode: BatteryTrainingMode = data?.batTrainingMode ?? 0
  const latestDeviceStatuses = useInstallationDeviceStatuses(installationId)

  return (
    <>
      <Stack direction={'row'} justifyContent={'space-between'} pl={{ xs: 2, sm: 0 }} pr={2} alignItems={'center'}>
        <Typography variant="h2" fontSize={'medium'}>
          {t('installation.summaryTable.components')}
        </Typography>
        {canEdit && (
          <IconButton
            sx={{
              mr: '2px', // to align with the checkboxes
            }}
            disabled={!Object.values(selectedDeviceIds).length}
            onClick={handleDelete}
          >
            <TrashIcon />
          </IconButton>
        )}
      </Stack>

      <List>
        {!devices.length ? (
          <ListItem>
            <ListItemText>
              <InfoText>{t('shell.noData')}</InfoText>
            </ListItemText>
          </ListItem>
        ) : (
          devices.map(device => (
            <DeviceListItem
              key={device.id}
              device={device}
              latestDeviceStatuses={latestDeviceStatuses}
              hasNonEmcDevices={hasNonEmcDevices}
              trainingMode={trainingMode}
              selectedDeviceIds={selectedDeviceIds}
              toggleDeviceSelection={toggleDeviceSelection}
            />
          ))
        )}
      </List>
    </>
  )
}

type DeviceListItemProps = {
  device: Device
  latestDeviceStatuses: DeviceStatusMap
  trainingMode: BatteryTrainingMode
  hasNonEmcDevices: boolean
  selectedDeviceIds: { [deviceId: string]: true }
  toggleDeviceSelection: (id: string) => void
}

const DeviceListItem: React.FC<DeviceListItemProps> = ({
  device,
  latestDeviceStatuses,
  trainingMode,
  hasNonEmcDevices,
  selectedDeviceIds,
  toggleDeviceSelection,
}) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const {
    featureFlags: { showDetailedDeviceErrorMessages },
  } = useFeatureFlagsContext()

  const { canPair, handlePairing } = useDevicePairing(device)

  const deviceWithLatestStatus = { ...device, ...latestDeviceStatuses.get(device.deviceId) }

  return (
    <ListItem divider>
      <ListItemIcon>
        <DeviceIcon device={device.deviceCategory} sx={{ fontSize: 35 }} />
      </ListItemIcon>

      <ListItemText>
        <Stack direction={'row'} alignItems={'center'}>
          <Stack flex={1}>
            <Box>{getDeviceDefaultName(device.name, device.deviceCategory, device.deviceId, t)}</Box>
            <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
              {device.pairingId || '-'}
            </Typography>
            <DeviceStatusIconMobile {...getDeviceParsedStatus(deviceWithLatestStatus, showDetailedDeviceErrorMessages, trainingMode)} />
          </Stack>

          <Box>
            <IconButton size="small" sx={{ mr: 1 }} disabled={!canEdit || !canPair} onClick={handlePairing} aria-label="pair device">
              {device.pairingId ? <PencilIcon /> : <LinkIcon />}
            </IconButton>
            {canEdit && (
              <Checkbox
                disabled={device.deviceCategory === 'EMC' && hasNonEmcDevices}
                checked={!!selectedDeviceIds[device.id]}
                onChange={() => toggleDeviceSelection(device.id)}
              />
            )}
          </Box>
        </Stack>
      </ListItemText>
    </ListItem>
  )
}
